import { Portal, useDisclosure, Popover } from '@chakra-ui/react';
import { RecordAuditInfo } from '@monetize/types/common';
import { FaRegClock } from 'react-icons/fa';
import { MdCalendarToday, MdHistory } from 'react-icons/md';
import {
  MBox,
  MCustomIconButton,
  MDivider,
  MFlex,
  MIcon,
  MSkeleton,
  MText,
  MVStack,
} from '.';
import { useGetTenantUserByIdAuditInfo } from '../../api/usersService';
import { DateDisplay } from './DateDisplay';

interface IRecordAuditInfoPopover {
  recordAuditInfo: RecordAuditInfo;
  tenantId: string;
  userId: string;
}

const RecordAuditInfoPopoverBodyLine = ({
  isCreated,
  date,
  userName,
}: {
  isCreated: boolean;
  date?: string;
  userName?: string;
}) => {
  if (!date) {
    return null;
  }

  return (
    <MBox w="100%">
      <MFlex alignItems="center" mb="1">
        <MIcon
          width={4}
          height={4}
          as={isCreated ? MdCalendarToday : FaRegClock}
          mr="2"
          color="tPurple.dark"
        />

        <MText fontSize="sm">{isCreated ? 'Created' : 'Last Modified'}</MText>
      </MFlex>

      <DateDisplay
        date={date}
        timezoneConfigKey="userTimezone"
        textProps={{ fontSize: 'sm', ml: 6 }}
      />
      {userName && (
        <MFlex alignItems="center" ml="6">
          <MText fontSize="sm">by {userName}</MText>
        </MFlex>
      )}
    </MBox>
  );
};

const RecordAuditInfoPopoverBody = ({
  recordAuditInfo,
  tenantId,
  userId,
}: IRecordAuditInfoPopover) => {
  const { data: lastModifiedByUser, isLoading: isLoadingLastModifiedByUser } =
    useGetTenantUserByIdAuditInfo({
      tenantId,
      userId: recordAuditInfo?.lastModifiedBy!,
      options: {
        enabled: !!recordAuditInfo?.lastModifiedBy && !!tenantId && !!userId,
        refetchOnWindowFocus: false,
      },
    });
  const { data: createdByUser, isLoading: isLoadingCreatedByUser } =
    useGetTenantUserByIdAuditInfo({
      tenantId,
      userId: recordAuditInfo?.createdBy!,
      options: {
        enabled: !!recordAuditInfo?.createdBy && !!tenantId && !!userId,
        refetchOnWindowFocus: false,
      },
    });

  return (
    <MVStack
      justifyContent="flex-start"
      alignItems="flex-start"
      gap="1"
      w="full"
    >
      {isLoadingCreatedByUser || isLoadingLastModifiedByUser ? (
        <>
          {Array.from({ length: 3 }).map((_, index) => (
            <MSkeleton key={index} height="14px" width="100%" />
          ))}
          <MDivider my="2" />
          {Array.from({ length: 3 }).map((_, index) => (
            <MSkeleton key={index} height="14px" width="100%" />
          ))}
        </>
      ) : (
        <MBox w="full">
          <RecordAuditInfoPopoverBodyLine
            isCreated
            date={recordAuditInfo?.createDate}
            userName={createdByUser?.name}
          />
          {recordAuditInfo?.modifyDate && recordAuditInfo?.createDate && (
            <MDivider my="2" />
          )}
          <RecordAuditInfoPopoverBodyLine
            isCreated={false}
            date={recordAuditInfo?.modifyDate}
            userName={lastModifiedByUser?.name}
          />
        </MBox>
      )}
    </MVStack>
  );
};

export const RecordAuditInfoPopover = ({
  recordAuditInfo,
  tenantId,
  userId,
}: IRecordAuditInfoPopover) => {
  const { onOpen, onClose, open } = useDisclosure();

  const handleOnOpen = () => {
    onOpen();
  };

  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      open={open}
      onOpenChange={(details) => {
        if (!details.open) {
          onClose();
        }
      }}
      restoreFocus
      size="sm"
      positioning={{
        strategy: 'fixed',
        placement: 'bottom-start',
        offset: { mainAxis: 0, crossAxis: 0 },
      }}
    >
      <MFlex align="center">
        <Popover.Trigger asChild>
          <MCustomIconButton
            containerSize={5}
            btnSize={4}
            variant="icon"
            verticalAlign="middle"
            icon={MdHistory}
            aria-label="View history"
            onClick={handleOnOpen}
          />
        </Popover.Trigger>
      </MFlex>

      <Portal>
        <Popover.Positioner>
          <Popover.Content>
            <Popover.Body>
              {open && (
                <RecordAuditInfoPopoverBody
                  recordAuditInfo={recordAuditInfo}
                  tenantId={tenantId}
                  userId={userId}
                />
              )}
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};
